import { Group, Text, UnstyledButton, Box, Stack } from '@mantine/core';
import { Edit3, Users } from 'react-feather';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RichTextEditor } from '../../../../components/common';
import type { RichTextControl } from '../../../../components/common/RichTextEditor/RichTextEditor';
import type {
  CoachPrepGroupContext,
  CoachLeaderNotesScope,
  CoachSessionLeaderNote,
} from '../../../../services/api/coachSessionPrep';
import {
  useLeaderNotesAutosave,
  type LeaderNotesAutosaveState,
} from './useLeaderNotesAutosave';

// MED-1094 — reduced toolbar (same set as the post-session notes editor,
// MED-1014): B / I / U | bullet & numbered list | link. Drops strikethrough and
// H1–H3.
const NOTES_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline'],
  ['bulletList', 'orderedList'],
  ['link'],
];

// MED-1094 — Leader Notes formatting, matched to the Figma (node 1991:3748).
const LEADER_NOTES_ACCENT = '#D97706';
const LEADER_NOTES_BORDER = '#E07B4C';
const LEADER_NOTES_BG = '#FFFBF7';
// Group tabs render as a joined segmented control (Figma): one outlined
// container, navy active segment, white inactive segments split by dividers.
const TAB_BORDER = '#E5E7EB';
const TAB_ACTIVE_BG = '#1B2B3A';
const TAB_INACTIVE_TEXT = '#6B7280';
const TAB_DOT = '#E07B4C';

/**
 * MED-793 / AC9 — Leader Notes panel rendered under each agenda step in
 * coach mode. Provides:
 *
 *   • A tab strip: "All Groups" + one tab per assigned group, with a red-dot
 *     indicator on tabs that already have a saved note for this block.
 *   • A WYSIWYG RichTextEditor (sanitized HTML) — the project's existing
 *     component (src/components/common/RichTextEditor), built on @tiptap/react.
 *   • MED-1081 — debounced autosave (via useLeaderNotesAutosave) with a
 *     "Saving…/Saved" status indicator in the editor toolbar row, replacing the
 *     old detached "Save" link. Text persists as the facilitator types, so it
 *     is no longer lost on "Mark as reviewed" or navigating away.
 *
 * AC8 multi-group: scope='all_groups' is the shared note across all the
 * coach's groups; scope='group' is an overlay specific to one group's tab.
 *
 * MED-1046 (E4.2a) — `singleGroup` collapses the panel for peer leaders, who
 * facilitate exactly one group: the group tab strip is hidden and every save
 * is forced to scope='all_groups' / groupId=null. Default false keeps the
 * coach multi-group behavior unchanged (AC7 no regression).
 */

interface LeaderNotesPanelProps {
  blockKey: string;
  groups: CoachPrepGroupContext[];
  /** Already filtered to notes matching this `blockKey`. */
  notes: CoachSessionLeaderNote[];
  onSave: (args: {
    blockKey: string;
    scope: CoachLeaderNotesScope;
    groupId: string | null;
    contentHtml: string;
  }) => Promise<void>;
  /**
   * MED-1046 — when true, render the single-field peer leader variant: no group
   * tab strip, and saves are always scope='all_groups' / groupId=null (AC4).
   */
  singleGroup?: boolean;
}

type ActiveTabKey = 'all_groups' | `group:${string}`;

export function LeaderNotesPanel({
  blockKey,
  groups,
  notes,
  onSave,
  singleGroup = false,
}: LeaderNotesPanelProps) {
  const [activeTab, setActiveTab] = useState<ActiveTabKey>('all_groups');
  const [draft, setDraft] = useState('');
  const {
    state: autosaveState,
    scheduleSave,
    flush,
  } = useLeaderNotesAutosave(onSave);

  // Index existing notes by tab key for quick reads.
  const noteByTab = useMemo(() => {
    const map = new Map<ActiveTabKey, CoachSessionLeaderNote>();
    for (const n of notes) {
      const key: ActiveTabKey =
        n.scope === 'all_groups' ? 'all_groups' : `group:${n.group_id}`;
      map.set(key, n);
    }
    return map;
  }, [notes]);

  // MED-1081 — seed the draft from saved content ONCE per editing identity
  // (this block + this tab). Deliberately NOT re-seeding on every `notes`
  // change: an autosave round-trip updates `notes`, and re-seeding then would
  // clobber the text the facilitator is still typing.
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${blockKey}::${activeTab}`;
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;
    setDraft(noteByTab.get(activeTab)?.content_html ?? '');
  }, [blockKey, activeTab, noteByTab]);

  const hasContentForTab = (key: ActiveTabKey): boolean => {
    const note = noteByTab.get(key);
    return Boolean(
      note && note.content_html.trim() && note.content_html.trim() !== '<p></p>'
    );
  };

  const scopeForActiveTab = (): {
    scope: CoachLeaderNotesScope;
    groupId: string | null;
  } =>
    activeTab === 'all_groups'
      ? { scope: 'all_groups', groupId: null }
      : { scope: 'group', groupId: activeTab.slice('group:'.length) };

  const handleChange = (html: string) => {
    setDraft(html);
    const { scope, groupId } = scopeForActiveTab();
    scheduleSave({ blockKey, scope, groupId, contentHtml: html });
  };

  // Persist the current tab's pending text before switching — switching
  // re-seeds the draft to the new tab's content, so an un-flushed edit on the
  // old tab would otherwise be dropped.
  const switchTab = (key: ActiveTabKey) => {
    if (key === activeTab) return;
    void flush();
    setActiveTab(key);
  };

  return (
    <Box mt="md">
      <Group gap={6} align="center" mb={6}>
        <Edit3 size={14} color={LEADER_NOTES_ACCENT} />
        <Text
          tt="uppercase"
          fz={12}
          fw={600}
          c={LEADER_NOTES_ACCENT}
          style={{ letterSpacing: 0.36 }}
        >
          Leader Notes
        </Text>
      </Group>

      {/* Tab strip — a joined segmented control (Figma). Hidden in single-group
          (peer leader) mode (MED-1046 AC4). */}
      {!singleGroup && (
        <Box
          mb="xs"
          style={{
            display: 'inline-flex',
            border: `1px solid ${TAB_BORDER}`,
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <NotesTab
            label="All Groups"
            icon={<Users size={13} />}
            first
            active={activeTab === 'all_groups'}
            hasContent={hasContentForTab('all_groups')}
            onClick={() => switchTab('all_groups')}
          />
          {groups.map(g => {
            const key: ActiveTabKey = `group:${g.group_id}`;
            return (
              <NotesTab
                key={g.group_id}
                label={g.group_name}
                active={activeTab === key}
                hasContent={hasContentForTab(key)}
                onClick={() => switchTab(key)}
              />
            );
          })}
        </Box>
      )}

      <Stack
        gap={0}
        style={{
          border: `1px solid ${LEADER_NOTES_BORDER}`,
          borderRadius: 10,
          background: LEADER_NOTES_BG,
        }}
      >
        <RichTextEditor
          value={draft}
          onChange={handleChange}
          placeholder="Add notes for this step…"
          minHeight={100}
          toolbar={NOTES_TOOLBAR}
          toolbarRight={<AutosaveStatus state={autosaveState} />}
        />
      </Stack>
    </Box>
  );
}

/** MED-1081 — inline autosave status, shown right-aligned in the editor toolbar. */
function AutosaveStatus({ state }: { state: LeaderNotesAutosaveState }) {
  const label =
    state === 'saving'
      ? 'Saving…'
      : state === 'saved'
        ? 'Saved'
        : state === 'error'
          ? 'Save failed — try again'
          : 'Autosaves as you type';
  return (
    <Text
      size="xs"
      c={state === 'error' ? 'red' : 'dimmed'}
      aria-live="polite"
      style={{ whiteSpace: 'nowrap' }}
    >
      {label}
    </Text>
  );
}

interface NotesTabProps {
  label: string;
  active: boolean;
  hasContent: boolean;
  onClick: () => void;
  /** Optional leading icon (e.g. the groups icon on "All Groups"). */
  icon?: ReactNode;
  /** First segment in the strip — omits the left divider. */
  first?: boolean;
}

function NotesTab({
  label,
  active,
  hasContent,
  onClick,
  icon,
  first = false,
}: NotesTabProps) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        padding: '6px 14px',
        background: active ? TAB_ACTIVE_BG : '#FFFFFF',
        color: active ? '#FFFFFF' : TAB_INACTIVE_TEXT,
        borderLeft: first ? undefined : `1px solid ${TAB_BORDER}`,
        fontWeight: 700,
        fontSize: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}
    >
      {icon}
      <span>{label}</span>
      {hasContent && (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            background: TAB_DOT,
            display: 'inline-block',
            marginLeft: 2,
          }}
        />
      )}
    </UnstyledButton>
  );
}
